import { useEffect, useMemo, useRef, useState } from "react"
import { ChartAlertPanel } from "@/components/chart/ChartAlertPanel"
import { ChartDrawingToolbar } from "@/components/chart/ChartDrawingToolbar"
import { ChartSurface } from "@/components/chart/ChartSurface"
import { ProvisionalBadge } from "@/components/chart/ProvisionalBadge"
import { Swatch } from "@/components/chart/ChartTooltip"
import { ChartTimeframeControls } from "@/components/chart/ChartTimeframeControls"
import { useChartAnnotations } from "@/hooks/useChartAnnotations"
import { getJson, patchJson } from "@/lib/api"
import { cn } from "@/lib/cn"
import { useChartTheme } from "@/lib/chart/ChartThemeContext"
import { percentChange } from "@/lib/chart/format"
import { INDICATOR_LABELS } from "@/lib/chart/indicators"
import {
  DEFAULT_CHART_INTERVAL,
  DEFAULT_CHART_RANGE,
  type ChartInterval,
  type ChartRange,
} from "@/lib/chart/timeframes"
import type { ChartMode, FuturesChartData, FuturesChartPreferences, IndicatorKey } from "@/types/futuresChart"

interface Props {
  initialData: FuturesChartData
  initialPreferences: FuturesChartPreferences
  signedIn: boolean
}

const MODE_LABELS: Record<ChartMode, string> = {
  candlestick: "Candles",
  ohlc: "OHLC",
  line: "Line",
}

export function FuturesChart({ initialData, initialPreferences, signedIn }: Props) {
  const chartTheme = useChartTheme()
  const [data, setData] = useState(initialData)
  const [preferences, setPreferences] = useState(initialPreferences)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAlertForm, setShowAlertForm] = useState(false)
  const [chartInterval, setChartInterval] = useState<ChartInterval>(DEFAULT_CHART_INTERVAL)
  const [range, setRange] = useState<ChartRange>(DEFAULT_CHART_RANGE)
  const chartRequestRef = useRef(0)
  const preferenceTimerRef = useRef<number | null>(null)

  const alertable = useMemo(
    () =>
      data.alertable ??
      (data.selected_contract_id ? { type: "Contract" as const, id: data.selected_contract_id } : null),
    [data.alertable, data.selected_contract_id],
  )

  const annotations = useChartAnnotations(alertable, signedIn)

  const selectedContract = data.contracts.find((contract) => contract.id === data.selected_contract_id)
  const latest = data.bars.at(-1)
  const latestChange = latest ? percentChange(latest.close, data.bars.at(-2)?.close ?? latest.open) : null
  const hasOpenInterest = data.bars.some((bar) => bar.open_interest != null)

  useEffect(() => {
    if (signedIn || !window.localStorage) return
    try {
      const saved = window.localStorage.getItem("arc:futures-chart-preferences")
      if (saved) setPreferences({ ...initialPreferences, ...JSON.parse(saved) } as FuturesChartPreferences)
    } catch {
      // Ignore unavailable or malformed browser storage.
    }
  }, [initialPreferences, signedIn])

  useEffect(
    () => () => {
      if (preferenceTimerRef.current !== null) window.clearTimeout(preferenceTimerRef.current)
    },
    [],
  )

  async function selectContract(contractId: number): Promise<void> {
    const requestId = ++chartRequestRef.current
    setLoading(true)
    setError(null)
    try {
      const response = await getJson<FuturesChartData>(
        `/api/commodities/${data.commodity.slug}/futures_chart?contract_id=${contractId}`,
      )
      if (requestId === chartRequestRef.current) setData(response)
    } catch (requestError) {
      if (requestId === chartRequestRef.current) {
        setError(requestError instanceof Error ? requestError.message : "Could not load contract")
      }
    } finally {
      if (requestId === chartRequestRef.current) setLoading(false)
    }
  }

  function updatePreferences(next: FuturesChartPreferences): void {
    setPreferences(next)
    if (signedIn) {
      if (preferenceTimerRef.current !== null) window.clearTimeout(preferenceTimerRef.current)
      preferenceTimerRef.current = window.setTimeout(() => {
        void patchJson("/api/chart_preferences", next).catch(() => {
          setError("Chart preference could not be saved")
        })
      }, 250)
    } else {
      window.localStorage?.setItem("arc:futures-chart-preferences", JSON.stringify(next))
    }
  }

  function setMode(mode: ChartMode): void {
    updatePreferences({ ...preferences, mode })
  }

  function toggleIndicator(indicator: IndicatorKey): void {
    const indicators = preferences.indicators.includes(indicator)
      ? preferences.indicators.filter((item) => item !== indicator)
      : [...preferences.indicators, indicator]
    updatePreferences({ ...preferences, indicators })
  }

  if (data.contracts.length === 0) {
    return (
      <section className="rounded-2xl border border-slate-800 bg-slate-950 px-6 py-12 text-center text-slate-400">
        Futures history is not available yet.
      </section>
    )
  }

  const displayError = error ?? annotations.error

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl shadow-slate-950/20">
      <div className="border-b border-slate-800 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 px-4 py-4 sm:px-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-400">Futures terminal</p>
            <div className="mt-1 flex items-baseline gap-3">
              <h2 className="truncate text-lg font-semibold text-white">{selectedContract?.ticker}</h2>
              {latest && (
                <>
                  <span className="font-mono text-xl text-slate-100">{formatPrice(latest.close)}</span>
                  <span
                    className={cn(
                      "font-mono text-xs",
                      (latestChange ?? 0) >= 0 ? "text-teal-300" : "text-rose-300",
                    )}
                  >
                    {(latestChange ?? 0) >= 0 ? "+" : ""}
                    {(latestChange ?? 0).toFixed(2)}%
                  </span>
                  <ProvisionalBadge bar={latest} />
                </>
              )}
            </div>
          </div>

          <select
            aria-label="Futures contract"
            value={data.selected_contract_id ?? ""}
            onChange={(event) => void selectContract(Number(event.target.value))}
            className="ml-auto rounded-lg border-slate-700 bg-slate-900 py-1.5 pl-3 pr-8 text-xs text-slate-200 focus:border-cyan-500 focus:ring-cyan-500"
          >
            {data.contracts.map((contract) => (
              <option key={contract.id} value={contract.id}>{contract.label}</option>
            ))}
          </select>

          {signedIn ? (
            <button
              type="button"
              onClick={() => setShowAlertForm((visible) => !visible)}
              className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-1.5 text-xs font-semibold text-cyan-300 transition hover:bg-cyan-500/20"
            >
              Alert {annotations.alerts.length > 0 && `(${annotations.alerts.length})`}
            </button>
          ) : (
            <a
              href="/users/sign_up"
              className="rounded-lg border border-cyan-500/40 px-3 py-1.5 text-xs font-semibold text-cyan-300"
            >
              Sign up for alerts
            </a>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg bg-slate-900 p-0.5">
            {(Object.keys(MODE_LABELS) as ChartMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={preferences.mode === mode}
                onClick={() => setMode(mode)}
                className={cn(
                  "rounded-md px-3 py-1 text-[11px] font-medium transition",
                  preferences.mode === mode
                    ? "bg-slate-700 text-white shadow"
                    : "text-slate-400 hover:text-slate-200",
                )}
              >
                {MODE_LABELS[mode]}
              </button>
            ))}
          </div>

          <ChartTimeframeControls
            interval={chartInterval}
            range={range}
            onIntervalChange={setChartInterval}
            onRangeChange={setRange}
          />

          <ChartDrawingToolbar
            toolMode={annotations.toolMode}
            onToolModeChange={annotations.setToolMode}
            showSuggestions={annotations.showSuggestions}
            onToggleSuggestions={() => annotations.setShowSuggestions((value) => !value)}
            signedIn={signedIn}
            onDeleteSelected={
              annotations.selectedDrawingId === null ? undefined : () => void annotations.removeSelectedDrawing()
            }
          />

          {(Object.keys(INDICATOR_LABELS) as IndicatorKey[]).map((indicator) => (
            <button
              key={indicator}
              type="button"
              aria-pressed={preferences.indicators.includes(indicator)}
              onClick={() => toggleIndicator(indicator)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] transition",
                preferences.indicators.includes(indicator)
                  ? "border-amber-400/40 bg-amber-400/10 text-amber-300"
                  : "border-slate-700 text-slate-500 hover:text-slate-300",
              )}
            >
              <Swatch color={preferences.indicators.includes(indicator) ? chartTheme.indicators[indicator] : "currentColor"} />
              {INDICATOR_LABELS[indicator]}
            </button>
          ))}

          <PaneToggle
            label="Volume"
            active={preferences.show_volume}
            onClick={() => updatePreferences({ ...preferences, show_volume: !preferences.show_volume })}
          />
          <PaneToggle
            label={hasOpenInterest ? "Open interest" : "Open interest · awaiting data"}
            active={preferences.show_open_interest && hasOpenInterest}
            onClick={() =>
              updatePreferences({ ...preferences, show_open_interest: !preferences.show_open_interest })
            }
          />
          {loading && <span className="ml-auto text-xs text-cyan-400">Loading…</span>}
        </div>

        {showAlertForm && selectedContract && latest && (
          <ChartAlertPanel
            symbol={selectedContract.ticker}
            currentPrice={latest.close}
            alerts={annotations.alerts}
            suggestions={annotations.suggestions}
            showSuggestions={annotations.showSuggestions}
            onCreateAlert={annotations.createAlert}
            onUpdateAlert={annotations.updateAlert}
            onRemoveAlert={annotations.removeAlert}
            onAcceptSuggestion={annotations.acceptSuggestion}
            onDismissSuggestion={annotations.dismissSuggestion}
          />
        )}
        {displayError && <p role="alert" className="mt-3 text-xs text-rose-300">{displayError}</p>}
      </div>

      <ChartSurface
        bars={data.bars}
        preferences={preferences}
        interval={chartInterval}
        range={range}
        overlays={annotations.overlays}
        toolMode={annotations.toolMode}
        drawingEnabled={signedIn}
        onPlaceDrawing={(price, originTime) => void annotations.placeDrawing(price, originTime)}
        onMoveDrawing={(overlayId, price) => void annotations.moveDrawing(overlayId, price)}
        onSelectDrawing={annotations.setSelectedOverlayId}
      />
    </section>
  )
}

function PaneToggle({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-lg border px-2.5 py-1 text-[11px] transition",
        active
          ? "border-violet-400/40 bg-violet-400/10 text-violet-300"
          : "border-slate-700 text-slate-500 hover:text-slate-300",
      )}
    >
      {label}
    </button>
  )
}

function formatPrice(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 })
}
